// Utilities for counting rows in uploaded files.
// Used to include a row count in the upload notification email.
//
// Row counting is best-effort:
//   - Returns null if the file can't be parsed (corrupt XLSX, malformed JSON,
//     unknown extension, empty file). Upload still proceeds; email will just
//     say "row count unavailable".
//   - Counts EXCLUDE the header row for CSV/XLSX (users think in terms of
//     data rows, not source-file rows).

import * as XLSX from 'xlsx';

/**
 * Count data rows in a file. Returns null if counting isn't possible.
 *
 * - .csv → line count minus header
 * - .xlsx / .xls → SheetJS parse, minus header
 * - .json → array length (if root is array) OR count of top-level keys
 * - anything else → null
 */
export async function countFileRows(file: File): Promise<number | null> {
  const ext = getExt(file.name);
  try {
    if (ext === 'csv') return await countCsvRows(file);
    if (ext === 'xlsx' || ext === 'xls') return await countXlsxRows(file);
    if (ext === 'json') return await countJsonRows(file);
    return null;
  } catch (err) {
    console.warn('[row-count] Failed to count rows', { file: file.name, err });
    return null;
  }
}

function getExt(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

// ─── CSV ────────────────────────────────────────────────────────────────────
//
// Counts non-empty lines minus 1 for the header. Handles \n, \r\n, and quoted
// newlines inside CSV fields (a "field with\nnewline" is one row, not two).
//
// For 500 MB files this reads the whole file into a string — acceptable
// because the browser has to hold the file anyway to upload it.

export async function countCsvRows(file: File): Promise<number | null> {
  const text = await file.text();
  if (!text.trim()) return 0;

  let rows = 0;
  let inQuotes = false;
  let currentRowHasContent = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (ch === '"') {
      // Handle escaped quote inside quoted field ("")
      if (inQuotes && text[i + 1] === '"') {
        i++;
        currentRowHasContent = true;
        continue;
      }
      inQuotes = !inQuotes;
      currentRowHasContent = true;
      continue;
    }

    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (currentRowHasContent) rows++;
      currentRowHasContent = false;
      // Skip \n after \r (Windows line endings)
      if (ch === '\r' && text[i + 1] === '\n') i++;
      continue;
    }

    if (ch !== ' ' && ch !== '\t') currentRowHasContent = true;
  }

  // Count trailing row if file didn't end with a newline
  if (currentRowHasContent) rows++;

  // Subtract header row (assume first row is header)
  return Math.max(0, rows - 1);
}

// ─── XLSX / XLS ─────────────────────────────────────────────────────────────
//
// Uses SheetJS. Counts rows returned by sheet_to_json which stops at the
// last populated row (Excel's stored dimensions can extend well past the
// last real data row — SheetJS handles this correctly).

export async function countXlsxRows(file: File): Promise<number | null> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return null;

  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return null;

  // sheet_to_json with default options treats the first row as header keys,
  // so the returned array length IS the data-row count (header already excluded).
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
    raw: false,
  });
  return rows.length;
}

// ─── JSON ───────────────────────────────────────────────────────────────────
//
// If the root is an array → array length.
// If the root is an object → 1 (single entry).
// Anything else (number, string, null, boolean) → null.

export async function countJsonRows(file: File): Promise<number | null> {
  const text = await file.text();
  if (!text.trim()) return 0;

  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed.length;
  if (parsed !== null && typeof parsed === 'object') return 1;
  return null;
}